import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { DataMainTemplate } from "@nocios/presentation-kit";
import Config from "src/Constants/Config";
import setCurrentPage from "src/Utils/setCurrentPage";

// Constants
const PAGE_ID = 2;
const NEXT_PAGE_ID = PAGE_ID + 1;
const BACK_PAGE_ID = PAGE_ID - 1;
const LAST_PAGE = false;
const BG_ID = 1;
const BG_TYPE = "jpg";

const LEFT_TITLE = "TELCO INSIGHTS SOLUTION";

// Image slider component
const ImageSlider = () => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const currentPage = useSelector((state: { appReducer: { currentPage: number } }) => state.appReducer.currentPage);

  useEffect(() => {
    setIsVisible(false); // Reset visibility when page changes
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);

    return () => clearTimeout(timer);
  }, [currentPage]); // Add currentPage as dependency

  const images = [1, 2, 3];
  const GAP = "calc(var(--width-95) * -1)";

  return (
    <div
      ref={containerRef}
      style={{
        position: "absolute",
        top: "15%", // Position above buttons
        right: "2%", // Start from left side
        width: "70%", // Cover most of the width
        height: "auto",
        pointerEvents: "none",
        zIndex: 10,
        display: "flex",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {images.map((index, i) => (
        <div
          key={index}
          style={{
            marginRight: i < images.length - 1 ? GAP : 0,
            width: "50%", // Adjust width for each image
            opacity: isVisible ? 1 : 0,
            transform: `translateX(${isVisible ? 0 : "100%"})`, // Changed to come from left
            transition: `opacity 0.7s ease-out, transform 0.7s ease-out`,
            transitionDelay: `${i * 0.3}s`,
          }}
        >
          <img
            src={`${Config.BASE_URL}/images/screen_${PAGE_ID}/right_${index}.png`}
            alt={`Presentation Data ${index}`}
            style={{
              width: "100%",
              height: "auto",
              display: "block",
              animation: `glow 3s ease-in-out infinite`,
            }}
          />
        </div>
      ))}
    </div>
  );
};

// Main data function that returns the template data
const DATA = (): DataMainTemplate => {
  const currentPage = useSelector((state: { appReducer: { currentPage: number } }) => state.appReducer.currentPage);

  const baseImageBG = `${Config.BASE_URL}/images/BG/`;

  const backButton =
    BACK_PAGE_ID > 0
      ? {
          label: "Back",
          type: "back" as const,
          startIcon: { icon: "arrow-right", color: "#fff" },
          onClick: () => setCurrentPage(BACK_PAGE_ID),
          style: {
            borderRadius: "45px",
            padding: "var(--width-5) var(--width-25)",
            gap: "var(--width-16)",
          },
        }
      : undefined;

  const nextButton = !LAST_PAGE
    ? {
        label: "Next",
        type: "next" as const,
        endIcon: { icon: "arrow-left" },
        onClick: () => setCurrentPage(NEXT_PAGE_ID),
        style: {
          borderRadius: "45px",
          padding: "var(--width-5) var(--width-25)",
          gap: "var(--width-16)",
        },
      }
    : undefined;

  const styleImg = {
    position: "absolute" as "absolute",
    top: 0,
    right: 0,
    width: "var(--width-1920)",
    height: "var(--height-1080)",
    transition: "opacity 0.5s ease-out",
  };

  const [animation, setAnimation] = useState(0);

  useEffect(() => {
    if (currentPage === PAGE_ID) {
      if (animation === 0) setTimeout(() => setAnimation(1), 2000);
      if (animation === 1) setTimeout(() => setAnimation(2), 600);
    } else setAnimation(0);
  }, [currentPage, animation]);

  return {
    bgVideo: {
      poster: `${baseImageBG}BG_${BG_ID}.${BG_TYPE}`,
      video: `${Config.BASE_URL}/videos/bg_${BG_ID}.mp4`,
    },
    gradient:
      "linear-gradient(270deg, rgba(21, 50, 121, 0.00) 11.77%, #153279 87.81%), linear-gradient(0deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.60) 100%)",
    title: LEFT_TITLE,
    description:
      "Our solution unifies telecom data to enhance\nanalytics and operational efficiency.\n\nDeliver personalized customer service, reduce\noperational costs, and create new revenue\nstreams, ultimately transforming the industry\nwith improved resilience and performance",
    rightSideComponent: (
      <>
        <ImageSlider key={currentPage} />
        <img
          style={{ ...styleImg, opacity: animation > 0 ? 1 : 0 }}
          src={`${Config.BASE_URL}/images/screen_${PAGE_ID}/right_4.png`}
          alt=""
        />
        <img
          style={{ ...styleImg, opacity: animation > 1 ? 1 : 0 }}
          src={`${Config.BASE_URL}/images/screen_${PAGE_ID}/right_5.png`}
          alt=""
        />
      </>
    ),
    next: nextButton,
    back: backButton,
  };
};

export default DATA;
